import {useMutation, useQuery} from '@apollo/client'

import {
  getNotificationCountRedis,
  getNotificationsCount,
  updateNotificationCountRedis,
} from '../queries/notificationQuery'
import {checkObjProps, logError} from '../utils/genericUtils'
import {useCallback, useEffect, useState} from 'react'

interface UseNotificationCountOptions {
  poll?: boolean
}

interface UseNotificationCountResult {
  count: number
  displayCount: number
  loading: boolean
  acknowledge: () => Promise<void>
}

const POLL_INTERVAL_MS = 1800000

export const useNotificationCount = ({
  poll = false,
}: UseNotificationCountOptions = {}): UseNotificationCountResult => {
  const [isWindowActive, setIsWindowActive] = useState(
    typeof document !== 'undefined' ? !document.hidden : true
  )

  useEffect(() => {
    if (!poll) return
    const handleVisibilityChange = () => {
      setIsWindowActive(!document.hidden)
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [poll])

  const {data: countData, loading} = useQuery(getNotificationsCount, {
    pollInterval: poll && isWindowActive ? POLL_INTERVAL_MS : 0,
  })

  const {data: redisData} = useQuery(getNotificationCountRedis)

  const [updateRedis] = useMutation(updateNotificationCountRedis)

  const count: number = countData?.notificationsCount ?? 0
  const redisCount: number = checkObjProps(
    redisData,
    ['getNotificationCount', 'notificationCount'],
    0
  )
  const displayCount = redisCount === count ? 0 : count

  const acknowledge = useCallback(async () => {
    try {
      await updateRedis({
        variables: {data: count},
        refetchQueries: [{query: getNotificationCountRedis}],
      })
    } catch (err) {
      logError('Error resetting notification count:', err)
    }
  }, [count, updateRedis])

  return {count, displayCount, loading, acknowledge}
}
